import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import type {
  LoginHistory,
  SecurityNotification,
  Passkey,
  TrustedDevice,
  AccountDeletionRequest,
  UserDataExport
} from '../types/database'

// Parse user agent to extract browser and OS
export function parseUserAgent(ua: string): { browser: string; os: string; deviceType: string } {
  let browser = 'Unknown'
  let os = 'Unknown'
  let deviceType = 'desktop'

  // Detect OS
  if (ua.includes('Windows')) os = 'Windows'
  else if (ua.includes('Mac OS')) os = 'macOS'
  else if (ua.includes('Linux')) os = 'Linux'
  else if (ua.includes('Android')) { os = 'Android'; deviceType = 'mobile' }
  else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS'
    deviceType = ua.includes('iPad') ? 'tablet' : 'mobile'
  }

  // Detect browser
  if (ua.includes('Firefox')) browser = 'Firefox'
  else if (ua.includes('Edg/')) browser = 'Edge'
  else if (ua.includes('Chrome')) browser = 'Chrome'
  else if (ua.includes('Safari')) browser = 'Safari'
  else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera'

  return { browser, os, deviceType }
}

// Check for suspicious login (new IP detection)
export async function checkSuspiciousLogin(userId: string): Promise<{
  isSuspicious: boolean
  isNewIp: boolean
  riskScore: number
  reasons: string[]
}> {
  try {
    // We don't have direct access to IP from browser, but the RPC can detect it server-side
    // For now, we'll use a placeholder and let the server handle IP detection
    const { data, error } = await supabase.rpc('check_suspicious_login', {
      p_user_id: userId,
      p_ip_address: 'client' // Server will get actual IP from request
    })

    if (error) {
      console.error('Error checking suspicious login:', error)
      return { isSuspicious: false, isNewIp: false, riskScore: 0, reasons: [] }
    }

    return {
      isSuspicious: data.is_suspicious,
      isNewIp: data.is_new_ip,
      riskScore: data.risk_score,
      reasons: data.reasons || []
    }
  } catch (error) {
    console.error('Error in checkSuspiciousLogin:', error)
    return { isSuspicious: false, isNewIp: false, riskScore: 0, reasons: [] }
  }
}

// Record known IP after successful login
export async function recordKnownIp(userId: string): Promise<void> {
  try {
    await supabase.rpc('record_known_ip', {
      p_user_id: userId,
      p_ip_address: 'client' // Server will get actual IP
    })
  } catch (error) {
    console.error('Error recording known IP:', error)
  }
}

// Standalone function to record login (can be called without hook)
export async function recordLoginEvent(userId: string, authMethod: string = 'email') {
  const ua = navigator.userAgent
  const { browser, os, deviceType } = parseUserAgent(ua)

  try {
    // Insert directly to avoid RPC overload conflict
    await supabase.from('login_history').insert({
      user_id: userId,
      user_agent: ua,
      device_type: deviceType,
      browser: browser,
      os: os,
      auth_method: authMethod,
      success: true
    })

    // Record this IP as known for the user
    await recordKnownIp(userId)
  } catch (error) {
    console.error('Error recording login:', error)
  }
}

export function useSecurity() {
  const { user } = useAuth()
  const [loginHistory, setLoginHistory] = useState<LoginHistory[]>([])
  const [notifications, setNotifications] = useState<SecurityNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [passkeys, setPasskeys] = useState<Passkey[]>([])
  const [trustedDevices, setTrustedDevices] = useState<TrustedDevice[]>([])
  const [deletionRequest, setDeletionRequest] = useState<AccountDeletionRequest | null>(null)
  const [loading, setLoading] = useState(true)
  const initialLoadDone = useRef(false)
  const currentUserId = useRef<string | null>(null)

  // Load all security data
  const loadSecurityData = useCallback(async (force = false) => {
    if (!user) return

    // If user changed, reset the flag
    if (currentUserId.current !== user.id) {
      initialLoadDone.current = false
      currentUserId.current = user.id
    }

    // Skip reload if already loaded, unless forced
    if (initialLoadDone.current && !force) return
    setLoading(true)

    try {
      const [
        historyRes,
        notifRes,
        passkeysRes,
        devicesRes,
        deletionRes
      ] = await Promise.all([
        supabase
          .from('login_history')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(50),
        supabase
          .from('security_notifications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(50),
        supabase
          .from('passkeys')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('trusted_devices')
          .select('*')
          .eq('user_id', user.id)
          .eq('revoked', false)
          .order('last_used_at', { ascending: false }),
        supabase
          .from('account_deletion_requests')
          .select('*')
          .eq('user_id', user.id)
          .eq('cancelled', false)
          .eq('executed', false)
          .maybeSingle()
      ])

      if (historyRes.data) setLoginHistory(historyRes.data)
      if (notifRes.data) {
        setNotifications(notifRes.data)
        setUnreadCount(notifRes.data.filter(n => !n.read).length)
      }
      if (passkeysRes.data) setPasskeys(passkeysRes.data)
      if (devicesRes.data) setTrustedDevices(devicesRes.data)
      if (deletionRes.data) setDeletionRequest(deletionRes.data)
      else setDeletionRequest(null)
      initialLoadDone.current = true
    } catch (error) {
      console.error('Error loading security data:', error)
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    loadSecurityData()
  }, [loadSecurityData])

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    const { error } = await supabase
      .from('security_notifications')
      .update({ read: true, read_at: new Date().toISOString() })
      .eq('id', notificationId)

    if (!error) {
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    }
    return { error }
  }

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user) return { error: new Error('Not authenticated') }

    const { error } = await supabase
      .from('security_notifications')
      .update({ read: true, read_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .eq('read', false)

    if (!error) {
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true, read_at: new Date().toISOString() }))
      )
      setUnreadCount(0)
    }
    return { error }
  }

  // Delete a notification
  const deleteNotification = async (notificationId: string) => {
    const notification = notifications.find(n => n.id === notificationId)
    const { error } = await supabase
      .from('security_notifications')
      .delete()
      .eq('id', notificationId)

    if (!error) {
      setNotifications(prev => prev.filter(n => n.id !== notificationId))
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    }
    return { error }
  }

  // Revoke a trusted device
  const revokeTrustedDevice = async (deviceId: string) => {
    const { error } = await supabase
      .from('trusted_devices')
      .update({ revoked: true, revoked_at: new Date().toISOString() })
      .eq('id', deviceId)

    if (!error) {
      setTrustedDevices(prev => prev.filter(d => d.id !== deviceId))
    }
    return { error }
  }

  // Revoke all trusted devices except current
  const revokeAllTrustedDevices = async () => {
    if (!user) return { error: new Error('Not authenticated') }

    const { error } = await supabase
      .from('trusted_devices')
      .update({ revoked: true, revoked_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .eq('revoked', false)

    if (!error) {
      setTrustedDevices([])
    }
    return { error }
  }

  // Delete a passkey
  const deletePasskey = async (passkeyId: string) => {
    // Get passkey name before deletion for notification
    const passkeyToDelete = passkeys.find(p => p.id === passkeyId)
    const passkeyName = passkeyToDelete?.name

    const { error } = await supabase
      .from('passkeys')
      .delete()
      .eq('id', passkeyId)

    if (!error) {
      setPasskeys(prev => prev.filter(p => p.id !== passkeyId))

      // Invalidate other sessions for security
      try {
        await supabase.auth.signOut({ scope: 'others' })
      } catch {
        // Ignore session invalidation errors
      }

      // Send notification (fire and forget)
      if (user?.email) {
        supabase.functions.invoke('send-notification', {
          body: {
            type: 'passkey-removed',
            email: user.email,
            passkeyName
          }
        }).catch(() => {})
      }
    }
    return { error }
  }

  // Rename a passkey
  const renamePasskey = async (passkeyId: string, name: string) => {
    const { error } = await supabase
      .from('passkeys')
      .update({ name })
      .eq('id', passkeyId)

    if (!error) {
      setPasskeys(prev =>
        prev.map(p => p.id === passkeyId ? { ...p, name } : p)
      )
    }
    return { error }
  }

  // Export user data (GDPR)
  const exportUserData = async (): Promise<{ data: UserDataExport | null; error: Error | null }> => {
    if (!user) return { data: null, error: new Error('Not authenticated') }

    try {
      const { data, error } = await supabase.rpc('export_user_data', {
        p_user_id: user.id
      })

      if (error) throw error
      return { data: data as UserDataExport, error: null }
    } catch (error) {
      return { data: null, error: error as Error }
    }
  }

  // Request account deletion
  const requestAccountDeletion = async (reason?: string) => {
    if (!user) return { data: null, error: new Error('Not authenticated') }

    try {
      const { data, error } = await supabase.rpc('request_account_deletion', {
        p_user_id: user.id,
        p_reason: reason || null
      })

      if (error) throw error

      // Reload data to get the new deletion request
      await loadSecurityData()
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error as Error }
    }
  }

  // Cancel account deletion
  const cancelAccountDeletion = async () => {
    if (!user) return { error: new Error('Not authenticated') }

    try {
      const { error } = await supabase.rpc('cancel_account_deletion', {
        p_user_id: user.id
      })

      if (error) throw error
      setDeletionRequest(null)
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  // Record a login (called after successful auth)
  const recordLogin = async (authMethod: string = 'email', userId?: string) => {
    const uid = userId || user?.id
    if (!uid) return

    const ua = navigator.userAgent
    const { browser, os, deviceType } = parseUserAgent(ua)

    try {
      await supabase.rpc('record_login', {
        p_user_id: uid,
        p_user_agent: ua,
        p_device_type: deviceType,
        p_browser: browser,
        p_os: os,
        p_auth_method: authMethod
      })
    } catch (error) {
      console.error('Error recording login:', error)
    }
  }

  // Get current device info
  const getCurrentDeviceInfo = () => {
    const ua = navigator.userAgent
    const { browser, os, deviceType } = parseUserAgent(ua)
    return { browser, os, deviceType, userAgent: ua }
  }

  // Add current device as trusted
  const addCurrentDeviceAsTrusted = async (deviceName?: string) => {
    if (!user) return { error: new Error('Not authenticated') }

    const { browser, os, deviceType } = getCurrentDeviceInfo()
    const token = crypto.randomUUID()
    const expiresAt = new Date()
    expiresAt.setDate(expiresAt.getDate() + 30) // 30 days

    const { data, error } = await supabase
      .from('trusted_devices')
      .insert({
        user_id: user.id,
        device_token: token,
        device_name: deviceName || `${browser} on ${os}`,
        device_type: deviceType,
        browser,
        os,
        last_used_at: new Date().toISOString(),
        expires_at: expiresAt.toISOString()
      })
      .select()
      .single()

    if (!error && data) {
      setTrustedDevices(prev => [data, ...prev])
    }

    return { data, error }
  }

  return {
    loginHistory,
    notifications,
    unreadCount,
    passkeys,
    trustedDevices,
    deletionRequest,
    loading,
    reload: () => loadSecurityData(true),
    markAsRead,
    markAllAsRead,
    deleteNotification,
    revokeTrustedDevice,
    revokeAllTrustedDevices,
    addCurrentDeviceAsTrusted,
    getCurrentDeviceInfo,
    deletePasskey,
    renamePasskey,
    exportUserData,
    requestAccountDeletion,
    cancelAccountDeletion,
    recordLogin
  }
}

// Hook specifically for passkey registration and authentication
export function usePasskeys() {
  // Check if WebAuthn is supported
  const isSupported = typeof window !== 'undefined' &&
    window.PublicKeyCredential !== undefined

  // Check if platform authenticator is available (Touch ID, Face ID, Windows Hello)
  const checkPlatformAuthenticator = async (): Promise<boolean> => {
    if (!isSupported) return false
    try {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
    } catch {
      return false
    }
  }

  // Helper to extract error message from FunctionsHttpError
  const extractFunctionError = async (error: any): Promise<string> => {
    if (error?.context) {
      try {
        const body = await error.context.json()
        return body?.error || error.message
      } catch {
        return error.message
      }
    }
    return error?.message || 'Unknown error'
  }

  // Generate registration options from server
  const getRegistrationOptions = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('passkey-register', {
        body: { action: 'options' }
      })
      if (error) {
        const message = await extractFunctionError(error)
        return { data: null, error: new Error(message) }
      }
      if (data?.error) {
        return { data: null, error: new Error(data.error) }
      }
      return { data, error: null }
    } catch (err: any) {
      const message = await extractFunctionError(err)
      return { data: null, error: new Error(message) }
    }
  }

  // Complete registration with credential
  const completeRegistration = async (credential: PublicKeyCredential, name?: string) => {
    const response = credential.response as AuthenticatorAttestationResponse

    try {
      const { data, error } = await supabase.functions.invoke('passkey-register', {
        body: {
          action: 'verify',
          credential: {
            id: credential.id,
            rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
            type: credential.type,
            response: {
              clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(response.clientDataJSON))),
              attestationObject: btoa(String.fromCharCode(...new Uint8Array(response.attestationObject)))
            }
          },
          name
        }
      })
      if (error) {
        const message = await extractFunctionError(error)
        return { data: null, error: new Error(message) }
      }
      if (data?.error) {
        return { data: null, error: new Error(data.error) }
      }
      return { data, error: null }
    } catch (err: any) {
      const message = await extractFunctionError(err)
      return { data: null, error: new Error(message) }
    }
  }

  // Get authentication options from server
  const getAuthenticationOptions = async (email?: string) => {
    const { data, error } = await supabase.functions.invoke('passkey-auth', {
      body: { action: 'options', email }
    })
    return { data, error }
  }

  // Complete authentication with credential
  const completeAuthentication = async (credential: PublicKeyCredential) => {
    const response = credential.response as AuthenticatorAssertionResponse

    const { data, error } = await supabase.functions.invoke('passkey-auth', {
      body: {
        action: 'verify',
        credential: {
          id: credential.id,
          rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
          type: credential.type,
          response: {
            clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(response.clientDataJSON))),
            authenticatorData: btoa(String.fromCharCode(...new Uint8Array(response.authenticatorData))),
            signature: btoa(String.fromCharCode(...new Uint8Array(response.signature))),
            userHandle: response.userHandle
              ? btoa(String.fromCharCode(...new Uint8Array(response.userHandle)))
              : null
          }
        }
      }
    })
    return { data, error }
  }

  return {
    isSupported,
    checkPlatformAuthenticator,
    getRegistrationOptions,
    completeRegistration,
    getAuthenticationOptions,
    completeAuthentication
  }
}
